import moment from "moment";
import * as d3 from "d3";
import { Device } from "../../utils/Device";
const MONTHLY_STOCK_RETURN = 0.07 / 12;
const MONTHLY_BOND_RETURN = 0.015 / 12;
const ASSETS_CORRELATION = 0.1;
const MONTHLY_STANDARD_DEVIATION_STOCKS = 0.15 / Math.sqrt(12);
const MONTHLY_STANDARD_DEVIATION_BONDS = 0.02 / Math.sqrt(12);
const MONTHLY_STANDARD_DEVIATION_STOCKS_SQUARED = Math.pow(MONTHLY_STANDARD_DEVIATION_STOCKS, 2);
const MONTHLY_STANDARD_DEVIATION_BONDS_SQUARED = Math.pow(MONTHLY_STANDARD_DEVIATION_BONDS, 2);
const PORTFOLIO_VOLATILITY = MONTHLY_STANDARD_DEVIATION_STOCKS_SQUARED +
    MONTHLY_STANDARD_DEVIATION_BONDS_SQUARED -
    2 *
        MONTHLY_STANDARD_DEVIATION_STOCKS *
        MONTHLY_STANDARD_DEVIATION_BONDS *
        ASSETS_CORRELATION;
const TEN_PERCENT_CONFIDENCE = 1.2816;
const FOURTY_PERCENT_CONFIDENCE = 0.2533;
export function generatePoints({ initial, monthly, risk, interval, }) {
    const startingCapital = parseInt(initial || "0", 10);
    const monthlySavings = parseInt(monthly || "0", 10);
    const stockPercentage = parseInt(risk || "0", 10) / 100;
    const bondsPercentage = 1 - stockPercentage;
    const investmentMonths = parseInt(interval, 10) * 12;
    const warningSeries = [];
    const veryBadSeries = [];
    const badSeries = [];
    const avgSeries = [];
    const goodSeries = [];
    const veryGoodSeries = [];
    const date = moment();
    warningSeries.push({
        worth: startingCapital,
        date: moment(date),
    });
    veryBadSeries.push({
        worth: startingCapital,
        date: moment(date),
    });
    badSeries.push({
        worth: startingCapital,
        date: moment(date),
    });
    avgSeries.push({
        worth: startingCapital,
        date: moment(date),
    });
    goodSeries.push({
        worth: startingCapital,
        date: moment(date),
    });
    veryGoodSeries.push({
        worth: startingCapital,
        date: moment(date),
    });
    let previousExpectedLogReturn = 1;
    let previousStandardDeviation = 1;
    let monthlySavingsAverage = monthlySavings;
    let monthlySavingsVeryGood = monthlySavings;
    let monthlySavingsGood = monthlySavings;
    let monthlySavingsBad = monthlySavings;
    let monthlySavingsVeryBad = monthlySavings;
    let dateToWorthMap = {};
    dateToWorthMap["time-" + moment(date).format()] = {
        veryBad: startingCapital,
        bad: startingCapital,
        average: startingCapital,
        good: startingCapital,
        veryGood: startingCapital,
    };
    for (let month = 1; month <= investmentMonths; month++) {
        date.add(1, "months");
        let expectedLogReturn = MONTHLY_BOND_RETURN * month +
            stockPercentage *
                (MONTHLY_STOCK_RETURN * month - MONTHLY_BOND_RETURN * month) +
            0.5 * stockPercentage * bondsPercentage * month * PORTFOLIO_VOLATILITY;
        let standardDeviation = Math.sqrt(month *
            (Math.pow(bondsPercentage, 2) *
                MONTHLY_STANDARD_DEVIATION_BONDS_SQUARED +
                Math.pow(stockPercentage, 2) *
                    MONTHLY_STANDARD_DEVIATION_STOCKS_SQUARED +
                stockPercentage *
                    bondsPercentage *
                    MONTHLY_STANDARD_DEVIATION_STOCKS *
                    MONTHLY_STANDARD_DEVIATION_BONDS *
                    ASSETS_CORRELATION));
        if (monthlySavings > 0 && month > 1) {
            monthlySavingsAverage +=
                monthlySavings * Math.exp(previousExpectedLogReturn);
            monthlySavingsVeryGood +=
                monthlySavings *
                    Math.exp(previousExpectedLogReturn +
                        TEN_PERCENT_CONFIDENCE * previousStandardDeviation);
            monthlySavingsGood +=
                monthlySavings *
                    Math.exp(previousExpectedLogReturn +
                        FOURTY_PERCENT_CONFIDENCE * previousStandardDeviation);
            monthlySavingsBad +=
                monthlySavings *
                    Math.exp(previousExpectedLogReturn -
                        FOURTY_PERCENT_CONFIDENCE * previousStandardDeviation);
            monthlySavingsVeryBad +=
                monthlySavings *
                    Math.exp(previousExpectedLogReturn -
                        TEN_PERCENT_CONFIDENCE * previousStandardDeviation);
        }
        let average = startingCapital * Math.exp(expectedLogReturn) + monthlySavingsAverage;
        let veryGood = startingCapital *
            Math.exp(expectedLogReturn + TEN_PERCENT_CONFIDENCE * standardDeviation) +
            monthlySavingsVeryGood;
        let good = startingCapital *
            Math.exp(expectedLogReturn + FOURTY_PERCENT_CONFIDENCE * standardDeviation) +
            monthlySavingsGood;
        let bad = startingCapital *
            Math.exp(expectedLogReturn - FOURTY_PERCENT_CONFIDENCE * standardDeviation) +
            monthlySavingsBad;
        let veryBad = startingCapital *
            Math.exp(expectedLogReturn - TEN_PERCENT_CONFIDENCE * standardDeviation) +
            monthlySavingsVeryBad;
        veryBadSeries.push({
            worth: veryBad,
            date: moment(date),
        });
        badSeries.push({
            worth: bad,
            date: moment(date),
        });
        avgSeries.push({
            worth: average,
            date: moment(date),
        });
        goodSeries.push({
            worth: good,
            date: moment(date),
        });
        veryGoodSeries.push({
            worth: veryGood,
            date: moment(date),
        });
        if (veryBad <= startingCapital) {
            warningSeries.push({
                worth: veryBad,
                date: moment(date),
            });
        }
        previousExpectedLogReturn = expectedLogReturn;
        previousStandardDeviation = standardDeviation;
        dateToWorthMap["time-" + date.format()] = {
            veryBad: veryBad,
            bad: bad,
            average: average,
            good: good,
            veryGood: veryGood,
        };
    }
    return {
        veryGoodSeries: veryGoodSeries,
        goodSeries: goodSeries,
        averageSeries: avgSeries,
        badSeries: badSeries,
        veryBadSeries: veryBadSeries,
        warningSeries: warningSeries,
        dateToWorthMap: dateToWorthMap,
        extremeValues: { min: 0, max: Number.POSITIVE_INFINITY },
    };
}
export function generateMinAndMax({ initial, monthly, interval, }) {
    let startingCapital = parseInt(initial || "0", 10);
    let monthlySavings = parseInt(monthly || "0", 10);
    let stockPercentage = 1;
    let bondsPercentage = 1 - stockPercentage;
    let investmentMonths = parseInt(interval, 10) * 12;
    let previousExpectedLogReturn = 1;
    let previousStandardDeviation = 1;
    let monthlySavingsVeryGood = monthlySavings;
    let veryGood = 0;
    for (let month = 1; month < investmentMonths; month++) {
        const expectedLogReturn = MONTHLY_BOND_RETURN * month +
            stockPercentage *
                (MONTHLY_STOCK_RETURN * month - MONTHLY_BOND_RETURN * month) +
            0.5 * stockPercentage * bondsPercentage * month * PORTFOLIO_VOLATILITY;
        let standardDeviation = Math.sqrt(month *
            (Math.pow(bondsPercentage, 2) *
                MONTHLY_STANDARD_DEVIATION_BONDS_SQUARED +
                Math.pow(stockPercentage, 2) *
                    MONTHLY_STANDARD_DEVIATION_STOCKS_SQUARED +
                stockPercentage *
                    bondsPercentage *
                    MONTHLY_STANDARD_DEVIATION_STOCKS *
                    MONTHLY_STANDARD_DEVIATION_BONDS *
                    ASSETS_CORRELATION));
        if (monthlySavings > 0 && month > 1) {
            monthlySavingsVeryGood +=
                monthlySavings *
                    Math.exp(previousExpectedLogReturn +
                        TEN_PERCENT_CONFIDENCE * previousStandardDeviation);
        }
        previousExpectedLogReturn = expectedLogReturn;
        previousStandardDeviation = standardDeviation;
        veryGood =
            startingCapital *
                Math.exp(expectedLogReturn + TEN_PERCENT_CONFIDENCE * standardDeviation) +
                monthlySavingsVeryGood;
    }
    return {
        min: 0,
        max: veryGood,
    };
}
function getUpperBound(current, multiple) {
    if (current === 0) {
        return multiple;
    }
    return Math.ceil(current / multiple) * multiple;
}
export function getBounds(maxProfit) {
    let multiple;
    let t;
    if (maxProfit > 100000000) {
        multiple = 1000000;
        t = getUpperBound(Math.ceil(maxProfit / multiple), 100);
    }
    else if (maxProfit > 10000000) {
        multiple = 1000000;
        t = getUpperBound(Math.ceil(maxProfit / multiple), 10);
    }
    else if (maxProfit > 1000000) {
        multiple = 1000000;
        t = getUpperBound(Math.ceil(maxProfit / multiple), 1);
    }
    else if (maxProfit > 100000) {
        multiple = 1000;
        t = getUpperBound(Math.ceil(maxProfit / multiple), 100);
    }
    else {
        multiple = 1;
        t = getUpperBound(Math.ceil(maxProfit / multiple), 100);
    }
    return {
        min: 0,
        max: t,
        value: t * multiple,
        multiple: multiple,
    };
}
function getMiddleDate(minDate, maxDate) {
    const timeDifference = (maxDate.getTime() - minDate.getTime()) / 2;
    const middleDay = minDate.getTime() + timeDifference;
    return new Date(middleDay);
}
export function getXDates(minDate, maxDate) {
    if (Device.isMobile()) {
        return [minDate, getMiddleDate(minDate, maxDate), maxDate];
    }
    else {
        let middle = getMiddleDate(minDate, maxDate);
        return [
            minDate,
            getMiddleDate(minDate, middle),
            middle,
            getMiddleDate(middle, maxDate),
            maxDate,
        ];
    }
}
export function getYTicks(lowerBound, upperBound) {
    const middleBound = lowerBound + (upperBound - lowerBound) / 2;
    const midLowerBound = lowerBound + (middleBound - lowerBound) / 2;
    const midUpperBound = middleBound + (upperBound - middleBound) / 2;
    if (Device.isMobile()) {
        return [lowerBound, midLowerBound, middleBound, midUpperBound, upperBound];
    }
    else {
        return [
            lowerBound,
            lowerBound + (midLowerBound - lowerBound) / 2,
            midLowerBound,
            midLowerBound + (middleBound - midLowerBound) / 2,
            middleBound,
            middleBound + (midUpperBound - middleBound) / 2,
            midUpperBound,
            midUpperBound + (upperBound - midUpperBound) / 2,
            upperBound,
        ];
    }
}
export const bisectDate = d3.bisector((d) => {
    return d.date;
}).left;
export function generateBands(x, y, points, startingCapital) {
    let fullBand = d3
        .area()
        .curve(d3.curveBasis)
        .x((d) => {
        return x(d.date);
    })
        .y1((d) => {
        return y(d.worth);
    })
        .y0((d, i) => {
        return y(points.veryBadSeries[i].worth);
    });
    let mediumBand = d3
        .area()
        .curve(d3.curveBasis)
        .x((d) => {
        return x(d.date);
    })
        .y1((d) => {
        return y(d.worth);
    })
        .y0((d, i) => {
        return y(points.badSeries[i].worth);
    });
    let textAnchor = d3
        .area()
        .curve(d3.curveBasis)
        .x((d) => {
        return x(d.date);
    })
        .y1((d) => {
        return y(d.worth);
    })
        .y0(y(startingCapital));
    return {
        fullBand,
        mediumBand,
        textAnchor,
    };
}
